import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { 
  GitCompare, 
  Folder, 
  Globe, 
  Lock, 
  Users, 
  MoreVertical, 
  Edit, 
  Trash2, 
  Eye,
  RefreshCw,
  AlertCircle,
  Download
} from 'lucide-react';
import { SavedComparisonGroup } from '../hooks/useSavedComparisonGroups';

interface ComparisonGroupsPanelProps {
  groups: SavedComparisonGroup[];
  loading: boolean;
  error: string | null;
  onLoadGroup: (groupId: string) => void;
  onEditGroup: (group: SavedComparisonGroup) => void;
  onEditGroupProducts: (group: SavedComparisonGroup) => void;
  onDeleteGroup: (groupId: string) => void;
  currentUser?: any;
}

const ComparisonGroupsPanel: React.FC<ComparisonGroupsPanelProps> = ({
  groups,
  loading,
  error,
  onLoadGroup,
  onEditGroup,
  onEditGroupProducts,
  onDeleteGroup,
  currentUser
}) => {
  const { t } = useTranslation();
  const [expandedGroup, setExpandedGroup] = useState<string | null>(null);
  const [actionMenuOpen, setActionMenuOpen] = useState<string | null>(null);

  const canEdit = (group: SavedComparisonGroup) => {
    // Permitir editar si es el propietario O si es un grupo público
    return currentUser && (group.createdBy === currentUser.uid || group.isPublic);
  };

  const isOwner = (group: SavedComparisonGroup) => {
    return currentUser && group.createdBy === currentUser.uid;
  };

  const toggleProductList = (groupId: string) => {
    setExpandedGroup(expandedGroup === groupId ? null : groupId);
  };

  const toggleActionMenu = (groupId: string) => {
    setActionMenuOpen(actionMenuOpen === groupId ? null : groupId);
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8">
        <div className="flex items-center justify-center space-x-3">
          <RefreshCw className="h-5 w-5 text-purple-500 animate-spin" />
          <span className="text-gray-600">{t('comparison_groups.loading')}</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8">
        <div className="text-center">
          <AlertCircle className="h-8 w-8 text-red-500 mx-auto mb-3" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            {t('comparison_groups.error_loading')}
          </h3>
          <p className="text-gray-600">{error}</p>
        </div>
      </div>
    );
  }

  if (groups.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center">
        <Folder className="h-12 w-12 text-gray-400 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-gray-900 mb-2">
          {t('comparison_groups.no_groups')}
        </h3>
        <p className="text-gray-600">
          {t('comparison_groups.no_groups_description')}
        </p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200">
      <div className="p-4 border-b border-gray-200">
        <div className="flex items-center space-x-2">
          <Folder className="h-5 w-5 text-purple-600" />
          <h3 className="text-lg font-semibold text-gray-900">
            {t('comparison_groups.saved_groups')}
          </h3>
          <span className="bg-purple-100 text-purple-800 text-xs px-2 py-1 rounded-full">
            {groups.length}
          </span>
        </div>
      </div>

      <div className="divide-y divide-gray-200">
        {groups.map((group) => (
          <div key={group.id} className="p-4 hover:bg-gray-50 transition-colors">
            <div className="flex items-start justify-between">
              {/* Información principal del grupo */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center space-x-2 mb-2">
                  <h4 className="text-sm font-medium text-gray-900 truncate">
                    {group.name}
                  </h4>
                  <div className="flex items-center space-x-1">
                    {group.isPublic ? (
                      <Globe className="h-3 w-3 text-green-500" />
                    ) : (
                      <Lock className="h-3 w-3 text-gray-500" />
                    )}
                  </div>
                </div>

                {group.description && (
                  <p className="text-sm text-gray-600 mb-2 line-clamp-2">
                    {group.description}
                  </p>
                )}

                <div className="flex items-center space-x-4 text-xs text-gray-500">
                  <div className="flex items-center space-x-1">
                    <GitCompare className="h-3 w-3" />
                    <span>{t('comparison_groups.products_count', { count: group.productCount })}</span>
                  </div>
                  <div className="flex items-center space-x-1">
                    <Users className="h-3 w-3" />
                    <span>{group.createdByName}</span>
                  </div>
                </div>

                {/* Expandable product list */}
                {expandedGroup === group.id && (
                  <div className="mt-3 p-3 bg-gray-50 rounded-lg">
                    <h5 className="text-xs font-medium text-gray-700 mb-2">
                      {t('comparison_groups.products_in_group')}:
                    </h5>
                    <div className="space-y-1 max-h-48 overflow-y-auto scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100">
                      {group.products.map((product, index) => {
                        const title = product.titulo || product.title || product.TITULO || t('comparison_groups.untitled_product');
                        const brand = product.Marca || product.marca || product.MARCA || '';
                        const price = product.precio_venta || product.sale_price || product.precio || 0;
                        
                        return (
                          <div key={index} className="flex items-center justify-between py-1 px-1">
                            <div className="flex items-center space-x-2 min-w-0 flex-1">
                              <div className="w-1.5 h-1.5 bg-purple-400 rounded-full flex-shrink-0"></div>
                              <span className="text-xs text-gray-700 truncate">
                                {brand && `${brand} - `}{title}
                              </span>
                            </div>
                            {price > 0 && (
                              <span className="text-xs text-gray-500 ml-2 flex-shrink-0">
                                ${price}
                              </span>
                            )}
                          </div>
                        );
                      })}
                    </div>
                    {group.products.length > 8 && (
                      <div className="text-xs text-gray-500 mt-2 text-center">
                        {t('comparison_groups.total_products', { count: group.products.length })}
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Menú de acciones */}
              <div className="flex items-center space-x-2 ml-4">
                <button
                  onClick={() => toggleProductList(group.id!)}
                  className="p-1 text-gray-400 hover:text-gray-600 transition-colors"
                  title={expandedGroup === group.id ? t('comparison_groups.hide_products') : t('comparison_groups.show_products')}
                >
                  <Eye className="h-4 w-4" />
                </button>

                <button
                  onClick={() => {
                    onLoadGroup(group.id!);
                  }}
                  className="p-1 text-purple-600 hover:text-purple-800 transition-colors"
                  title={t('comparison_groups.load_group')}
                >
                  <Download className="h-4 w-4" />
                </button>

                <div className="relative">
                  <button
                    onClick={() => toggleActionMenu(group.id!)}
                    className="p-1 text-gray-400 hover:text-gray-600 transition-colors"
                  >
                    <MoreVertical className="h-4 w-4" />
                  </button>

                  {actionMenuOpen === group.id && (
                    <div className="absolute right-0 top-full mt-1 w-32 bg-white rounded-lg shadow-lg border border-gray-200 z-10">
                      {canEdit(group) && (
                        <>
                          <button
                            onClick={() => {
                              onEditGroup(group);
                              setActionMenuOpen(null);
                            }}
                            className="w-full px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center space-x-2"
                          >
                            <Edit className="h-3 w-3" />
                            <span>{t('common.edit')}</span>
                          </button>
                          <button
                            onClick={() => {
                              onEditGroupProducts(group);
                              setActionMenuOpen(null);
                            }}
                            className="w-full px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center space-x-2"
                          >
                            <GitCompare className="h-3 w-3" />
                            <span>{t('comparison_groups.edit_products')}</span>
                          </button>
                          {isOwner(group) && (
                            <button
                              onClick={() => {
                                onDeleteGroup(group.id!);
                                setActionMenuOpen(null);
                              }}
                              className="w-full px-3 py-2 text-left text-sm text-red-600 hover:bg-red-50 flex items-center space-x-2"
                            >
                              <Trash2 className="h-3 w-3" />
                              <span>{t('common.delete')}</span>
                            </button>
                          )}
                        </>
                      )}
                      {!canEdit(group) && (
                        <div className="px-3 py-2 text-xs text-gray-500 text-center">
                          {t('comparison_groups.view_only')}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default ComparisonGroupsPanel; 